import { Fragment, type ComponentProps, type ReactNode } from 'react'
import { Card, CardBody, CardHead, Field, Input, Textarea } from './ui'
import type { IconName } from './icons'
import { RefUploader } from './RefUploader'
import { ProductAssetPickerButton } from './ProductAssetPicker'
import type { ProductAsset } from '@/api/types'

/**
 * 「产品信息」section —— **全站唯一一份**（2026-10-10）。
 *
 * ⚠️ 组件名叫 `ProductInfoSection` 而不是 `ProductInfo` —— 详情页已有一个
 * 同名**类型** `ProductInfo`（`lib/detailPrompts` 的产品信息契约），
 * 组件与它重名会当场撞车。
 *
 * 用户口径（banner 页整页截图）：「这些页面的产品信息应该合成一个 section，
 * 产品标题，产品描述，产品图，做出一个组件」。
 *
 * 在此之前，这三样在生成页里被**拆成了两张卡**（典型是 Banner：一张「产品名称」、
 * 一张「产品参考图」，中间还隔着别的字段；社媒图 / 详情页也各写各的排法）。
 * 同一份「这个东西是什么」被摊在好几处，用户填一项产品信息要来回找。
 * 现在收成一张卡、一个组件：**标题 → 描述 → 产品图**，顺序与用户口述一致。
 *
 * ## 为什么是「卡片」而不是别的形状
 *
 * 它要能直接塞进各生成页的左栏（`GeneratorFrame` 的 form 槽），而那些页面的
 * 基本单位就是 `Card`。所以本组件自带卡头 + 卡体，调用方一行接入：
 *
 * ```tsx
 * <ProductInfoSection
 *   name={form.productName} onNameChange={(v) => patch({ productName: v })}
 *   description={form.sellingPoints} onDescriptionChange={(v) => patch({ sellingPoints: v })}
 *   refs={form.refs} onRefsChange={(refs) => patch({ refs })}
 * />
 * ```
 *
 * 2026-10-10 补：**服饰图**（`/professional/*`）与**数字人**（`/digital-human`）
 * 也接了进来。这两页的形状和图片页不一样，所以本组件多了三个开关 ——
 * 每一个都对应一页**真实存在**的排法，不是为「以后可能要」预留的：
 *
 *   · `refsFirst` —— 数字人那张「产品与画面」是**产品图 → 产品名称 → 画面描述**，
 *     产品图在前；服饰图也是**先给素材、最后才写文字**。它们只是把「图」这一槽
 *     挪到名称之前，字段本身一个没变。
 *   · `onToggle` / `open` / `collapsedHint` —— 数字人那张卡是**页内折叠**的
 *     （三张卡叠一页放不下，只有必填的挑脸卡配占首屏）。折叠形态沿用
 *     `CardHead` 那一份实现，本组件只是把 `open` 受控地转接过去。
 *   · `refsSource` / `refsFootnote` —— 服饰图的主参考图带着候选条（`source`）
 *     与「已入图床 XXX」脚注，这两个槽位照原样转接给 `RefUploader`。
 *
 * ## 三种槽位都是**可选**的 —— 不是所有页都有这三样
 *
 * · 文案工作台（`/copywriting`）**没有图片槽位**，不传 `onRefsChange`；
 * · 服饰图**没有「产品标题」这一格**（表单由 feature 驱动），不传 `onNameChange`，
 *   它只借这张卡的外壳与 `refs` / `foot` 两个槽位；
 * · 数字人的克隆档连产品图与画面描述都没有，只留产品名称 —— 那两槽在该档不传。
 *
 * ⚠️ **产品图上传只走 `RefUploader` 一份实现**（项目头号铁律，见 smoke-nav 的
 * 硬断言）—— 本组件自己不写任何文件选择框，`wrap="none"` 让它以「字段」形态
 * 长在这张卡里，而不是再套一层自带卡头的小卡。
 *
 * ⚠️ 「从商品库选择」按钮只在传了 `onPickAsset` 时渲染，且**挂在标题这一格**
 * （它是带入的入口字段）—— 与各页原来的接入方式一致，语义不变。
 */
export function ProductInfoSection({
  title = '产品信息',
  icon = 'box',
  headRight,
  open,
  onToggle,
  collapsedHint,
  name,
  onNameChange,
  nameLabel = '产品名称',
  namePlaceholder = '如：华数云硅藻土沥水垫',
  nameRequired = false,
  nameHint,
  onPickAsset,
  description,
  onDescriptionChange,
  descriptionLabel = '产品描述',
  descriptionPlaceholder = '如：0.5s 吸水、可折叠、耐高温 120℃',
  descriptionHint,
  descriptionRows = 3,
  refs,
  onRefsChange,
  refsLabel = '产品图',
  refsHint,
  refsMax = 6,
  refsRequired,
  refsDropLabel = '拖拽或点击上传产品图',
  refsDropHint = '支持多选 · 可拖拽排序 · 第一张作为主图',
  refsEmptyHint,
  refsFirstBadge = '主图',
  refsSource,
  refsFootnote,
  refsFirst = false,
  foot,
  children,
}: {
  title?: string
  icon?: IconName
  /** 卡头右侧槽位（本页特有的按钮，如「AI 填充」） */
  headRight?: ReactNode
  /**
   * 受控折叠态。传了 `onToggle` 即变成可折叠卡（与 `CardHead` 同一套判据）——
   * 折叠状态必须由调用方持有，否则切页签就丢（见 `CardHead` 的说明）。
   */
  open?: boolean
  onToggle?: () => void
  /** 折叠起来时卡头下面那行说明（展开态不显示） */
  collapsedHint?: ReactNode
  name?: string
  /** 不传则**不渲染标题槽**（如服饰图表单里没有「产品名称」这一格） */
  onNameChange?: (v: string) => void
  nameLabel?: string
  namePlaceholder?: string
  nameRequired?: boolean
  nameHint?: ReactNode
  /** 传入即渲染「从商品库选择」，选中后把整条档案交回调用方 */
  onPickAsset?: (asset: ProductAsset) => void
  description?: string
  /** 不传则**不渲染描述槽**（如服饰图没有独立的「产品描述」字段） */
  onDescriptionChange?: (v: string) => void
  descriptionLabel?: string
  descriptionPlaceholder?: string
  descriptionHint?: ReactNode
  descriptionRows?: number
  refs?: string[]
  /** 不传则**不渲染产品图槽**（如文案工作台 / 数字人克隆档没有图片位） */
  onRefsChange?: (urls: string[]) => void
  refsLabel?: string
  refsHint?: string
  refsMax?: number
  refsRequired?: boolean
  refsDropLabel?: string
  refsDropHint?: string
  refsEmptyHint?: string
  refsFirstBadge?: string
  /** 参考图的另一条来源（候选条）—— 原样转接给 `RefUploader` 的 `source` */
  refsSource?: ComponentProps<typeof RefUploader>['source']
  /** 产品图下面的脚注（如「已入图床 XXX」）—— 转接给 `RefUploader` 的 `footnote` */
  refsFootnote?: ReactNode
  /**
   * 产品图排在名称 / 描述**之前** —— 数字人「产品与画面」与服饰图都是
   * 「先给素材、再命名 / 描述」的页。默认 `false`（图片页那一套：名称在最上）。
   */
  refsFirst?: boolean
  /** 卡片末尾的追加字段（目标人群 / 场景倾向这类「产品信息之外」的输入） */
  children?: ReactNode
  /**
   * 排在**产品图之后**的追加内容 —— 与 `children` 的区别是位置：
   * 详情页的「参考版式图」与「批量提示」必须落成**产品图的下一个兄弟**，
   * 而它们既不是产品信息的字段、也不该插在产品图之前。
   * 服饰图的「第二参考图 + 模特/场景设定 + 补充要求」同理：一律排在主参考图之后。
   */
  foot?: ReactNode
}) {
  const nameSlot = onNameChange ? (
    <Field
      label={nameLabel}
      required={nameRequired}
      hint={nameHint}
      right={onPickAsset ? <ProductAssetPickerButton onPick={onPickAsset} /> : undefined}
    >
      <Input
        value={name ?? ''}
        placeholder={namePlaceholder}
        onChange={(e) => onNameChange(e.target.value)}
      />
    </Field>
  ) : null

  const descriptionSlot = onDescriptionChange ? (
    <Field label={descriptionLabel} hint={descriptionHint}>
      <Textarea
        rows={descriptionRows}
        value={description ?? ''}
        placeholder={descriptionPlaceholder}
        onChange={(e) => onDescriptionChange(e.target.value)}
      />
    </Field>
  ) : null

  const refsSlot = onRefsChange ? (
    <RefUploader
      wrap="none"
      label={refsLabel}
      required={refsRequired}
      fieldHint={refsHint}
      urls={refs ?? []}
      onChange={onRefsChange}
      max={refsMax}
      dropLabel={refsDropLabel}
      dropHint={refsDropHint}
      emptyHint={refsEmptyHint}
      firstBadge={refsFirstBadge}
      source={refsSource}
      footnote={refsFootnote}
    />
  ) : null

  /**
   * 槽位按**名字**取，顺序由 `order` 决定 —— 而不是写死一棵 JSX 树。
   *
   * 两页的排法不同（图片页「名称 → 描述 → 图」，数字人 / 服饰图「图在最前」），
   * 但共用同一组槽位实现。写死一棵树的话，`refsFirst` 那一页就得把整段 JSX
   * 再抄一遍 —— 抄漏某槽的可选判断是本项目最常见的一类 bug（截图上看不出来）。
   */
  const slots: Record<string, ReactNode> = {
    name: nameSlot,
    description: descriptionSlot,
    children,
    refs: refsSlot,
    foot,
  }
  const order = refsFirst
    ? ['refs', 'name', 'description', 'children', 'foot']
    : ['name', 'description', 'children', 'refs', 'foot']

  const folded = !!onToggle && !open

  return (
    <Card className={folded ? 'card--folded' : ''}>
      <CardHead
        title={title}
        icon={icon}
        right={headRight}
        open={onToggle ? open : undefined}
        onToggle={onToggle}
        hint={folded ? collapsedHint : undefined}
      />
      {folded ? null : (
        <CardBody>
          {order.map((k) => (
            <Fragment key={k}>{slots[k]}</Fragment>
          ))}
        </CardBody>
      )}
    </Card>
  )
}
